import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import {
    CloudAccount,
    CloudCustomer,
    CloudEnvironment,
    DevOpsActionAndParameter,
    CreateDevOpsActionRequest,
    CreateDevOpsActionResponse,
    DevOpsActionParameter,
    ErmDevOpsApiClientService
} from 'erm-sdk';
import * as _ from 'lodash';
import cronstrue from 'cronstrue';

export class CreateDevopsActionBaseComponent {
    isSaving: boolean = false;

    selectedTriggerDate?: Date;
    selectedTriggerTimeHours?: number;
    selectedTriggerTimeMinute?: number;
    selectedTriggerTimeSecond?: number;
    selectedTriggerType?: string;

    selectedDevOpsActionAndParameter?: DevOpsActionAndParameter;
    selectedDevOpsParameterArray?: DevOpsActionParameter[];
    selectedActionParameterArray?: DevOpsActionParameter[];

    hours: number[];
    minutes: number[];
    seconds: number[];
    triggerTypes: string[];

    cloudCutomers?: CloudCustomer[];
    cloudAccounts?: CloudAccount[];
    cloudEnvironments?: CloudEnvironment[];
    supportedRegions?: string[];

    createDevOpsActionRequest: CreateDevOpsActionRequest;
    createDevOpsActionResponse?: CreateDevOpsActionResponse;

    scheduleExpressionHint: string = "Minutes Hours Day-of-month Month Day-of-week Year";

    constructor(public ermDevOpsApiClient: ErmDevOpsApiClientService,) {
        // initialize time
        this.hours = new Array(24).fill(0).map((v, i) => { return i });
        this.minutes = new Array(60).fill(0).map((v, i) => { return i });
        this.seconds = new Array(60).fill(0).map((v, i) => { return i });

        // trigger kind (web local variable only)
        this.triggerTypes = ["Schedule Expression", "Trigger Date Time"];

        this.createDevOpsActionRequest = {} as CreateDevOpsActionRequest;
    }


    onDateChanged(event: MatDatepickerInputEvent<Date>): void {
        this.onTimeChanged();
    }

    onTimeChanged(): void {
        if (this.selectedTriggerDate) {
            if (!this.selectedTriggerTimeHours) {
                this.selectedTriggerTimeHours = this.hours[0];
            }
            if (!this.selectedTriggerTimeMinute) {
                this.selectedTriggerTimeMinute = this.minutes[0];
            }
            if (!this.selectedTriggerTimeSecond) {
                this.selectedTriggerTimeSecond = this.seconds[0];
            }
            this.selectedTriggerDate.setHours(this.selectedTriggerTimeHours);
            this.selectedTriggerDate.setMinutes(this.selectedTriggerTimeMinute);
            this.selectedTriggerDate.setSeconds(this.selectedTriggerTimeSecond);
            this.selectedTriggerDate.setMilliseconds(0);
        }
        this.createDevOpsActionRequest.triggerTime = this.selectedTriggerDate;
    }

    onActionNameChanged(): void {
        if (!this.selectedDevOpsActionAndParameter) {
            return;
        }

        // reset parameter array
        this.selectedActionParameterArray = undefined;
        this.selectedDevOpsParameterArray = undefined;

        // reset trigger type
        this.selectedTriggerType = undefined;
        this.onTriggerTypeChanged();

        switch (this.selectedDevOpsActionAndParameter.actionScope) {
            case 'Customer':
                this.createDevOpsActionRequest.accountId = undefined;
                this.createDevOpsActionRequest.environmentId = undefined;
                this.createDevOpsActionRequest.region = undefined;
                break;
            case 'Account':
                this.createDevOpsActionRequest.customerId = undefined;
                this.createDevOpsActionRequest.environmentId = undefined;
                this.createDevOpsActionRequest.region = undefined;
                break;
            case 'Environment':
                this.createDevOpsActionRequest.customerId = undefined;
                this.createDevOpsActionRequest.accountId = undefined;
                this.createDevOpsActionRequest.region = undefined;
                break;
            case 'Region':
                this.createDevOpsActionRequest.customerId = undefined;
                this.createDevOpsActionRequest.accountId = undefined;
                this.createDevOpsActionRequest.environmentId = undefined;
                break;
            case 'None':
            default:
                this.createDevOpsActionRequest.customerId = undefined;
                this.createDevOpsActionRequest.accountId = undefined;
                this.createDevOpsActionRequest.environmentId = undefined;
                this.createDevOpsActionRequest.region = undefined;
                break;
        }
    }

    private resetScheduleExpression() {
        this.createDevOpsActionRequest.scheduleExpression = undefined;
    }

    private resetTriggerTimeAndDate() {
        this.selectedTriggerDate = undefined;
        this.selectedTriggerTimeHours = undefined;
        this.selectedTriggerTimeMinute = undefined;
        this.selectedTriggerTimeSecond = undefined;
        this.createDevOpsActionRequest.triggerTime = undefined;
    }

    onTriggerTypeChanged(): void {
        if (this.selectedTriggerType === "Schedule Expression") {
            // Reset Trigger Time and Date
            this.resetTriggerTimeAndDate();
        }
        else if (this.selectedTriggerType === "Trigger Date Time") {
            // Reset Schedule Expression
            this.resetScheduleExpression();
        }
        else {
            // reset all
            this.resetScheduleExpression();
            this.resetTriggerTimeAndDate();
        }
    }

    onDevOpsParameterArrayChanged(): void {
        // make a deep clone of DevOps Parameters
        if (this.selectedDevOpsParameterArray) {
            this.selectedActionParameterArray = _.cloneDeep<DevOpsActionParameter[]>(this.selectedDevOpsParameterArray);
        }
    }

    onScheduleExpressionChanged() {
        if (this.createDevOpsActionRequest.scheduleExpression && this.createDevOpsActionRequest.scheduleExpression.split(' ').length >= 5) {
            // convert cron expression to human readable string
            this.scheduleExpressionHint = cronstrue.toString(this.createDevOpsActionRequest.scheduleExpression);
        }
        else {
            this.scheduleExpressionHint = "Minutes Hours Day-of-month Month Day-of-week Year";
        }
    }

    async save(): Promise<void> {
        try {
            if (!this.selectedDevOpsActionAndParameter) {
                return;
            }

            if (this.createDevOpsActionRequest.scheduleExpression && this.createDevOpsActionRequest.scheduleExpression.split(' ').length !== 6) {
                alert("Only Schedule Expression of length 6 is supported");
                return;
            }

            this.isSaving = true;
            // parameters is equivalent of C# IDictionary<string,Object>
            const parameters: { [key: string]: any; } = {};

            // set parameter
            if (this.selectedDevOpsActionAndParameter.isParametersSupported && this.selectedActionParameterArray) {
                this.selectedActionParameterArray.forEach(parameter => {
                    if (parameter.name) {
                        parameters[parameter.name] = parameter.value;
                    }
                });
            }
            this.createDevOpsActionRequest.parameters = parameters;
            this.createDevOpsActionRequest.actionName = this.selectedDevOpsActionAndParameter.actionName;
            const createDevOpsActionResponse = await this.ermDevOpsApiClient.createDevOpsAction(this.createDevOpsActionRequest);
            this.createDevOpsActionResponse = createDevOpsActionResponse.body as CreateDevOpsActionResponse;
        } finally {
            this.isSaving = false;
        }
    }
}